import { useBlocNext } from "@blac/react";
import { ProgressIndicator } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { IonContent } from "@ionic/react";
import type { FC } from "react";
import React, { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import Loader from "src/ui/components/Loader/Loader";
import type { SignupCustomPageParameters } from "src/ui/components/SignupCustomContent/SignupCustom.types";
import SignupCustomCampaign from "src/ui/components/SignupCustomContent/SignupCustomCampaign";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import { CenterEl, ErrorPageLayout } from "../SentryBoundary/ErrorPage";
import Translate from "../Translate/Translate";
import { NON_BREAKING_SPACE } from "src/constants/misc";

const ContentWrap = styled.div`
  label: ContentWrap;
  display: grid;
  place-items: center;
  --p-top: calc(76px);
  --p-bottom: 2rem;
  min-height: 100svh;
  padding: var(--p-top) 0 0 0;
  box-sizing: border-box;

  & > nine-bg {
    flex: 1;
  }

  .fade-wrap {
    width: 100%;
    height: 100%;
  }
`;

const IonContentStyled = styled(IonContent)`
  background-color: var(--color-cream);
  min-height: 100vh;
  min-height: 100svh;

  padding-bottom: var(--keyboard-height, 0px);
`;

const SavingOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: var(--color-cream);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const SAVING_TIMEOUT_MS = 20_000;

const SignupCustomContent: FC = () => {
  const navigate = useNavigate();
  const parameters = useParams<SignupCustomPageParameters>();
  const [
    {
      campaign,
      step,
      currentStepValid,
      isAvailable,
      savingIndicator,
      transitionPending
    },
    {
      currentStep,
      campaignNameIsValid,
      campaignReleaseDate,
      campaignPrettyName,
      isDemoFunnel,
      getActiveCampaign,
      hideSaving
    }
  ] = useBlocNext(SignupCustomBloc);
  const scrollRef = React.useRef<HTMLIonContentElement>(null);
  const [loadingState, { isLoading }] = useBlocNext(LoadingCubit);
  const checkingStep = useMemo(
    () => isLoading(LoadingKey.checkCustomSignupStepDataComplete),
    [loadingState]
  );

  const isSavingVisible = Boolean(savingIndicator ?? transitionPending);

  useEffect(() => {
    if (!isSavingVisible) {
      return;
    }

    const timeoutId = setTimeout(() => {
      reportErrorSentry(
        new Error("Funnel saving indicator timeout after 20 seconds"),
        {
          campaign,
          step,
          savingIndicatorText: savingIndicator?.text?.toString() ?? null,
          transitionPending
        }
      );
      window.location.reload();
    }, SAVING_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isSavingVisible]);

  const displayReleaseDate = useMemo(() => {
    if (!campaignReleaseDate) {
      return "";
    }
    return campaignReleaseDate
      .toDate()
      .toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      })
      .split(" ")
      .join(NON_BREAKING_SPACE);
  }, [campaignReleaseDate]);

  const textContext = getActiveCampaign().availabilityChecks?.textContext ?? "";

  // redirect when step changes
  useEffect(() => {
    if (!currentStep) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();
    const path = `${isDemoFunnel ? "/demo" : ""}/signup/${campaign}/${step}?${queryString}`;

    if (step === parameters.step) {
      return;
    }

    navigate(path, {
      replace: !parameters.step
    });
  }, [currentStep, parameters, isDemoFunnel]);

  useEffect(() => {
    if (scrollRef.current) {
      window.nineConfig = {
        ...window.nineConfig,
        mainScrollTarget: scrollRef.current
      };
    }
    return () => {
      window.nineConfig = {
        ...window.nineConfig,
        mainScrollTarget: document.body
      };
    };
  }, []);

  useEffect(() => {
    if (!campaignNameIsValid) {
      const params = new URLSearchParams(window.location.search);
      const queryString = params.toString();
      window.location.replace(`/signup/universal?${queryString}`);
    }
  }, [campaignNameIsValid]);

  return (
    <IonContentStyled ref={scrollRef} scrollEvents>
      <ContentWrap>
        <Helmet>
          {currentStep && (
            <title>
              {translate("page_name")} - {campaignPrettyName} -{" "}
              {currentStep.name}
            </title>
          )}
        </Helmet>

        {(checkingStep || typeof isAvailable === "undefined") && (
          <Loader fixed overContent background fullPage />
        )}
        {currentStepValid && isAvailable === false && (
          <ErrorPageLayout>
            <div>
              <CenterEl>
                <nine-heading>
                  <nine-spacer s="xs"></nine-spacer>
                  <h4 className="">
                    <Translate
                      msg="funnel.unavailable.title"
                      variables={{
                        context: textContext
                      }}
                    />
                  </h4>
                  <nine-spacer s="md"></nine-spacer>
                  <p className="m0 color-c-60">
                    <Translate
                      msg="funnel.unavailable.description"
                      variables={{
                        availableDate: displayReleaseDate,
                        context: textContext
                      }}
                    />
                  </p>
                </nine-heading>
              </CenterEl>
            </div>
          </ErrorPageLayout>
        )}

        {currentStepValid && isAvailable === true && <SignupCustomCampaign />}
      </ContentWrap>

      <AnimatePresence>
        {(savingIndicator ?? transitionPending) && (
          <SavingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ProgressIndicator
              indeterminate
              complete={savingIndicator?.complete && !transitionPending}
              onComplete={hideSaving}
              loadTime={savingIndicator?.durationMs ?? undefined}
              img={savingIndicator?.img}
            >
              <p className="as-subhead2 color-c-80">
                {savingIndicator?.text ?? (
                  <Translate msg="savingIndicator.info" />
                )}
              </p>
            </ProgressIndicator>
          </SavingOverlay>
        )}
      </AnimatePresence>
    </IonContentStyled>
  );
};

export default SignupCustomContent;
